import { useState, type ReactNode } from "react";

import { Button } from "~/components/Button";
import { CopyButton } from "~/components/CopyButton";
import { DiffLines, unifiedDiffLines } from "~/components/DiffLines";
import { ExtraFieldFormatter } from "~/components/ExtraFieldFormatter";
import { GitButton } from "~/components/GitButton";
import { Icon } from "~/components/icon";
import { KubectlCopyCommand } from "~/routes/_index/components/KubectlCopyCommand";
import type {
  Cluster,
  Operation,
  OperationLog,
  StatusTransition,
} from "~/types";
import { getDatadogTidyJobLogsUrl } from "~/utils";
import { getOperationAlias } from "~/utils/commandConfig";
import { formatUtcTimestamp } from "~/utils/time";

type OperationArguments = Record<string, unknown>;

type OnCreateOperation = (operation: string, args: OperationArguments) => void;

interface BaseOperationDetailProps {
  cluster: Cluster;
  operation: Operation;
  onCreateOperation?: OnCreateOperation;
}

interface OperationDetailProps extends BaseOperationDetailProps {
  previousOperation: Operation | null;
}

export function OperationDetail(props: OperationDetailProps) {
  switch (props.operation.name) {
    case "set_config":
      return <SetConfigOperationDetail {...props} />;
    case "tidy":
      return <TidyOperationDetail {...props} />;
    case "upgrade":
      return <UpgradeOperationDetail {...props} />;
    default:
      return <DefaultOperationDetail {...props} />;
  }
}

/**
 * Shared operation detail layout
 */

interface OperationDetailLayoutProps extends BaseOperationDetailProps {
  children: ReactNode;
  timeline: ReactNode;
  logsAction?: ReactNode;
}

function ArgumentSection({
  args,
  argumentActions,
}: {
  args: OperationArguments;
  argumentActions?: Record<string, ReactNode>;
}) {
  return (
    <>
      <h4 className="mb-2 text-sm font-medium text-gray-900">Arguments</h4>
      <div className="space-y-3">
        {Object.entries(args).map(([key, value]) => (
          <div key={key} className="text-sm">
            <div className="flex items-start gap-2">
              <span className="font-medium text-gray-500">{key}</span>
              {argumentActions?.[key]}
            </div>
            <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded bg-gray-100 p-2 font-mono text-sm text-gray-900">
              {typeof value === "object"
                ? JSON.stringify(value, null, 2)
                : String(value)}
            </pre>
          </div>
        ))}
      </div>
    </>
  );
}

function OperationDetailLayout({
  cluster,
  operation,
  children,
  timeline,
  logsAction,
  onCreateOperation,
}: OperationDetailLayoutProps) {
  const args = operation.arguments as OperationArguments;
  const opLink = `${window.location.origin}/clusters/${cluster.id}#op-${operation.id}`;

  return (
    <div className="mt-2 flex gap-4 border-t border-gray-200 pt-2">
      <div className="flex-1">
        <div className="mb-2 flex items-center gap-2">
          <h4 className="text-sm font-medium text-gray-900">
            Operation ID: {operation.id}
          </h4>
          <CopyButton
            value={opLink}
            label="Copy link to operation"
            icon="Link"
            className="rounded p-1 transition-colors hover:bg-gray-100"
          />
        </div>
        {getOperationAlias(operation.name, args) ? (
          <h4 className="mb-2 text-sm font-medium text-gray-900">
            Operation: <span className="font-mono">{operation.name}</span>
          </h4>
        ) : null}
        <h4 className="text-sm font-medium text-gray-900">Approval oneliner</h4>
        <KubectlCopyCommand operationId={operation.id} />
        {children}
        <Button
          onClick={(event) => {
            event.preventDefault();
            onCreateOperation?.(operation.name, args);
          }}
        >
          Retry
        </Button>
      </div>
      <div className="flex-1">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">Logs</h4>
          {logsAction}
        </div>
        {timeline}
      </div>
    </div>
  );
}

function OperationTimeline({
  cluster,
  statusTransitions,
  operationLogs,
  onCreateOperation,
}: {
  cluster: Cluster;
  statusTransitions: StatusTransition[];
  operationLogs: OperationLog[];
  onCreateOperation?: OnCreateOperation;
}) {
  const [showExtraData, setShowExtraData] = useState<Record<number, boolean>>(
    {},
  );
  const timelineEvents = [
    ...statusTransitions.map((transition, index) => ({
      timestamp: new Date(transition.created_at).toISOString(),
      isTransition: true,
      content: `Status changed to ${transition.status}`,
      attrs: [],
      extra: transition.extra,
      transitionIndex: index,
    })),
    ...operationLogs.map((log) => ({
      timestamp: log.timestamp,
      isTransition: false,
      content: log.message,
      attrs: log.attrs,
      extra: null,
      transitionIndex: -1,
    })),
  ].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return (
    <div className="h-64 overflow-auto rounded bg-gray-900 p-2 font-mono text-xs text-gray-300">
      <div className="space-y-2">
        {timelineEvents.map((event, index) => (
          <div key={index} className="flex flex-col">
            <span className="text-gray-500">
              {formatUtcTimestamp(event.timestamp)}
            </span>
            <div className="flex items-start gap-2">
              <span
                className={
                  event.isTransition ? "text-blue-300" : "text-gray-300"
                }
              >
                {event.content}
              </span>
              {event.isTransition &&
              event.extra &&
              typeof event.extra === "object" &&
              Object.keys(event.extra).length > 0 ? (
                <button
                  onClick={(clickEvent) => {
                    clickEvent.preventDefault();
                    setShowExtraData((current) => ({
                      ...current,
                      [event.transitionIndex]: !current[event.transitionIndex],
                    }));
                  }}
                  className="rounded px-1.5 py-0.5 text-xs text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                  title="Show extra data"
                >
                  {showExtraData[event.transitionIndex] ? "▼" : "▶"} Extra
                </button>
              ) : null}
            </div>
            {event.isTransition &&
            showExtraData[event.transitionIndex] &&
            event.extra ? (
              <ExtraFieldFormatter
                extraData={event.extra}
                onCreateOperation={onCreateOperation}
                cluster={cluster}
              />
            ) : null}
            <div className="ml-4 text-gray-400">
              {event.attrs.map((attr, attrIndex) => (
                <div key={attrIndex}>
                  <span className="text-gray-400">{attr.key}:</span>{" "}
                  <span className="text-gray-300">{attr.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StandardOperationTimeline({
  cluster,
  operation,
  onCreateOperation,
}: BaseOperationDetailProps) {
  return (
    <OperationTimeline
      cluster={cluster}
      statusTransitions={operation.status_transitions}
      operationLogs={operation.operation_logs}
      onCreateOperation={onCreateOperation}
    />
  );
}

/**
 * Default (generic) operation detail
 */

function DefaultOperationDetail({
  cluster,
  operation,
  onCreateOperation,
}: BaseOperationDetailProps) {
  const args = operation.arguments as OperationArguments;

  return (
    <OperationDetailLayout
      cluster={cluster}
      operation={operation}
      timeline={
        <StandardOperationTimeline
          cluster={cluster}
          operation={operation}
          onCreateOperation={onCreateOperation}
        />
      }
      onCreateOperation={onCreateOperation}
    >
      <ArgumentSection args={args} />
    </OperationDetailLayout>
  );
}

/**
 * Tidy operation detail layout
 */

function TidyOperationDetail({
  cluster,
  operation,
  onCreateOperation,
}: BaseOperationDetailProps) {
  const args = operation.arguments as OperationArguments;

  return (
    <OperationDetailLayout
      cluster={cluster}
      operation={operation}
      logsAction={<TidyLogsLink cluster={cluster} operation={operation} />}
      timeline={
        <StandardOperationTimeline
          cluster={cluster}
          operation={operation}
          onCreateOperation={onCreateOperation}
        />
      }
      onCreateOperation={onCreateOperation}
    >
      <ArgumentSection args={args} />
    </OperationDetailLayout>
  );
}

function TidyLogsLink({
  cluster,
  operation,
}: {
  cluster: Cluster;
  operation: Operation;
}) {
  const tidyJobLogsUrl = getDatadogTidyJobLogsUrl(cluster, operation.id);
  if (tidyJobLogsUrl === null) {
    return null;
  }

  return (
    <a
      href={tidyJobLogsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-100"
      onClick={(event) => event.stopPropagation()}
    >
      <Icon name="ScrollText" size={12} />
      Pod output in Datadog
      <Icon name="ExternalLink" size={11} />
    </a>
  );
}

/**
 * Upgrade operation detail
 */

function UpgradeOperationDetail({
  cluster,
  operation,
  previousOperation,
  onCreateOperation,
}: OperationDetailProps) {
  const args = operation.arguments as OperationArguments;
  const targetVersion = args.target_version;
  const previousTargetVersion = previousOperation?.arguments.target_version;
  const gitDiffUrl =
    previousOperation?.name === "upgrade" &&
    typeof previousTargetVersion === "string" &&
    typeof targetVersion === "string" &&
    previousTargetVersion !== targetVersion
      ? `https://github.com/turbopuffer/turbopuffer/compare/${previousTargetVersion}...${targetVersion}`
      : null;

  return (
    <OperationDetailLayout
      cluster={cluster}
      operation={operation}
      timeline={
        <StandardOperationTimeline
          cluster={cluster}
          operation={operation}
          onCreateOperation={onCreateOperation}
        />
      }
      onCreateOperation={onCreateOperation}
    >
      <ArgumentSection
        args={args}
        argumentActions={
          gitDiffUrl
            ? {
                target_version: (
                  <GitButton
                    icon="GitCompare"
                    title="View diff on GitHub"
                    href={gitDiffUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }
            : undefined
        }
      />
    </OperationDetailLayout>
  );
}

/**
 * Set config operation detail
 */

type ConfigDiff = Record<string, string>;

function SetConfigOperationDetail(props: OperationDetailProps) {
  const configDiff = configDiffFromTransitions(
    props.operation.status_transitions,
  );
  if (configDiff === null) {
    return <DefaultOperationDetail {...props} />;
  }

  const args = props.operation.arguments as OperationArguments;
  return (
    <OperationDetailLayout
      cluster={props.cluster}
      operation={props.operation}
      timeline={<SetConfigOperationTimeline {...props} />}
      onCreateOperation={props.onCreateOperation}
    >
      <SetConfigPayload args={args} configDiff={configDiff} />
    </OperationDetailLayout>
  );
}

function SetConfigOperationTimeline({
  cluster,
  operation,
  onCreateOperation,
}: BaseOperationDetailProps) {
  const statusTransitions = operation.status_transitions.map((transition) =>
    transition.extra?.["turbopuffer-settings-overrides"] != null
      ? { ...transition, extra: null }
      : transition,
  );

  return (
    <OperationTimeline
      cluster={cluster}
      statusTransitions={statusTransitions}
      operationLogs={operation.operation_logs}
      onCreateOperation={onCreateOperation}
    />
  );
}

function SetConfigPayload({
  args,
  configDiff,
}: {
  args: OperationArguments;
  configDiff: ConfigDiff;
}) {
  const remainingArgs = Object.fromEntries(
    Object.entries(args).filter(([key]) => key !== "data"),
  );

  return (
    <>
      <h4 className="mb-2 text-sm font-medium text-gray-900">Changes</h4>
      {Object.keys(configDiff).length === 0 ? (
        <p className="mb-3 text-sm text-gray-500">No configuration changes.</p>
      ) : (
        <div className="mb-3 space-y-3">
          {Object.entries(configDiff).map(([fileName, patch]) => (
            <div key={fileName} className="text-sm">
              <div className="font-medium text-gray-500">{fileName}</div>
              <ConfigDiffBlock patch={patch} />
            </div>
          ))}
        </div>
      )}
      {Object.keys(remainingArgs).length > 0 ? (
        <ArgumentSection args={remainingArgs} />
      ) : null}
    </>
  );
}

function ConfigDiffBlock({ patch }: { patch: string }) {
  return (
    <DiffLines
      lines={unifiedDiffLines(patch)}
      className="mt-1 max-h-60 rounded bg-slate-950 py-2"
    />
  );
}

function configDiffFromTransitions(
  statusTransitions: StatusTransition[],
): ConfigDiff | null {
  for (const transition of statusTransitions) {
    const configDiff = transition.extra?.config_diff as ConfigDiff | undefined;
    if (configDiff !== undefined) {
      return configDiff;
    }
  }
  return null;
}
